// Frontend-only MOCK data + localStorage-backed “fake backend” for Phase 1.
// NOTE: This is intentionally designed with interfaces that map 1:1 to future API calls,
// so we can replace the implementation later with minimal refactors.

const LS_KEY = "589iq_mock_v1";
const LS_SESSION_KEY = "589iq_session_v1";
const LS_ANALYTICS_KEY = "589iq_analytics_v1";

const nowIso = () => new Date().toISOString();

const seed = () => {
  const tenantId = "tenant_1";
  const accountId = "acct_demo";
  const contactId = "ct_demo";

  return {
    tenant: {
      id: tenantId,
      name: "589 Labs.ai",
      created_at: nowIso(),
      updated_at: nowIso(),
    },
    accounts: [
      {
        id: accountId,
        tenant_id: tenantId,
        name: "Demo Manufacturing Co.",
        industry: "medical",
        created_at: nowIso(),
        updated_at: nowIso(),
      },
    ],
    contacts: [
      {
        id: contactId,
        tenant_id: tenantId,
        account_id: accountId,
        full_name: "Jordan Lee",
        title: "COO",
        email: "jordan@example.com",
        phone: "+1 (555) 013-2048",
        created_at: nowIso(),
        updated_at: nowIso(),
      },
    ],
    leads: [
      {
        id: "lead_demo",
        tenant_id: tenantId,
        created_at: nowIso(),
        updated_at: nowIso(),
        source: "website",
        utm: { source: "direct", medium: "none", campaign: "" },
        vertical: "medical",
        company: "Demo Manufacturing Co.",
        full_name: "Jordan Lee",
        email: "jordan@example.com",
        phone: "+1 (555) 013-2048",
        notes: "Looking to automate order-to-cash + customer support triage.",
        status: "new", // new | contacted | converted
        first_response_at: null,
      },
    ],
    prospects: [
      {
        id: "pros_demo",
        tenant_id: tenantId,
        account_id: accountId,
        primary_contact_id: contactId,
        assigned_to_user_id: "user_consultant",
        stage: "scheduled",
        created_at: nowIso(),
        updated_at: nowIso(),
        flags: { security_sensitive: true, regulated: true },
        payment: {
          detailed_assessment_paid: false,
          roadmap_paid: false,
          deposit_paid: false,
        },
      },
    ],
    interactions: [
      {
        id: "ix_demo_1",
        tenant_id: tenantId,
        prospect_id: "pros_demo",
        type: "note",
        created_at: nowIso(),
        created_by: "user_consultant",
        summary: "Initial inbound lead — awaiting booking.",
      },
    ],
    meetings: [
      {
        id: "mtg_demo",
        tenant_id: tenantId,
        prospect_id: "pros_demo",
        type: "initial_free_call",
        scheduled_at: new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString(),
        status: "scheduled",
        calendly_event_id: "mock_evt_123",
        zoom_link: "",
        created_at: nowIso(),
        updated_at: nowIso(),
      },
    ],
    response_sets: [],
    responses: [],
    score_summaries: [],
    notifications: [],
  };
};

const loadDb = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return seed();
    return JSON.parse(raw);
  } catch {
    return seed();
  }
};

const saveDb = (db) => {
  localStorage.setItem(LS_KEY, JSON.stringify({ ...db, updated_at: nowIso() }));
};

const uid = (prefix) => `${prefix}_${Math.random().toString(16).slice(2)}${Date.now()}`;

export const INDUSTRIES = [
  { key: "general", label: "GENERAL" },
  { key: "medical", label: "MEDICAL" },
  { key: "construction", label: "CONSTRUCTION" },
  { key: "education", label: "EDUCATION" },
  { key: "gaming", label: "CASINO GAMING" },
  { key: "events", label: "EVENTS" },
  { key: "legal", label: "LEGAL" },
  { key: "hospitality", label: "HOSPITALITY" },
];

export const INDUSTRY_CONTENT = {
  general: {
    use_cases: [
      "Cross-functional workflow automation",
      "Document processing and data extraction",
      "Customer communication copilots",
    ],
    kpis: [
      "Reduce manual processing 30–50%",
      "Cut response times 20–40%",
      "Improve throughput without headcount",
    ],
    risks: [
      "Data privacy and access controls",
      "Change management and adoption",
      "Quality assurance for AI outputs",
    ],
  },
  medical: {
    use_cases: [
      "Prior auth & eligibility workflow copilots",
      "Patient intake summarization + routing",
      "Revenue cycle exception handling automation",
    ],
    kpis: [
      "Reduce cycle time 20–40%",
      "Cut rework/denials 10–25%",
      "Improve first-contact resolution",
    ],
    risks: [
      "PHI handling & access controls",
      "Audit trails for AI-assisted outputs",
      "Human approval gates for production changes",
    ],
  },
  construction: {
    use_cases: [
      "RFI / submittal tracking and auto-routing",
      "Safety compliance document processing",
      "Project cost estimation and change order analysis",
    ],
    kpis: [
      "Reduce RFI response time 40–60%",
      "Cut compliance review backlog 30–50%",
      "Improve change order accuracy 20–35%",
    ],
    risks: [
      "Document version control across field/office",
      "Integration with legacy project management tools",
      "Accuracy of AI in safety-critical contexts",
    ],
  },
  education: {
    use_cases: [
      "Case management triage + next-best action",
      "Knowledge base search across policy + curriculum",
      "Procurement + invoice exception automation",
    ],
    kpis: [
      "Faster response SLAs",
      "Higher service desk resolution rates",
      "Reduced admin time per request",
    ],
    risks: [
      "Student data access boundaries",
      "Content safety + prompt governance",
      "Vendor/data residency constraints",
    ],
  },
  gaming: {
    use_cases: [
      "Player support triage + deflection",
      "Fraud/chargeback workflow automation",
      "Live-ops incident summarization + handoffs",
    ],
    kpis: [
      "Lower cost per ticket",
      "Reduced time-to-resolution",
      "Improved retention via faster support",
    ],
    risks: [
      "Abuse vectors (prompt injection)",
      "PII boundaries and data minimization",
      "Operational safety for auto-actions",
    ],
  },
  events: {
    use_cases: [
      "Attendee support + onsite issue routing",
      "Vendor coordination + run-of-show updates",
      "Sponsor reporting automation",
    ],
    kpis: [
      "Fewer escalations onsite",
      "Faster vendor response loops",
      "Higher sponsor renewal confidence",
    ],
    risks: [
      "Temporary staff access control",
      "Auditability of operational decisions",
      "Data sharing with partners",
    ],
  },
  legal: {
    use_cases: [
      "Matter intake triage + conflict checks support",
      "Contract review workflow acceleration",
      "Billing narrative standardization",
    ],
    kpis: [
      "Shorter intake cycles",
      "Reduced review time per agreement",
      "More consistent billing narratives",
    ],
    risks: [
      "Privilege boundaries",
      "Strict tenant isolation",
      "Human-in-the-loop requirements",
    ],
  },
  hospitality: {
    use_cases: [
      "Guest request routing + resolution copilots",
      "Staff scheduling exception workflows",
      "Inventory + purchasing anomaly alerts",
    ],
    kpis: [
      "Higher guest satisfaction",
      "Lower response time",
      "Reduced waste and stock-outs",
    ],
    risks: [
      "Frontline device access",
      "Operational controls for auto-actions",
      "Third-party system boundaries",
    ],
  },
};

export const OFFERS = [
  {
    key: "score",
    title: "Preliminary Assessment",
    price: "FREE",
    subtitle: "Smart intake to identify your top workflow opportunities",
    output: "Opportunity Snapshot + Top 3 workflows + Readiness indicators + Recommended next step",
    stage: "Start",
  },
  {
    key: "assessment",
    title: "In-Depth Assessment",
    price: "$4,999",
    subtitle: "Deep-dive analysis with prioritized action plan",
    output:
      "Workflow analysis + Prioritized opportunities + Readiness & security lens + Action plan + Portal setup",
    tooltip:
      "Pricing based on business size, workflow complexity, systems landscape, data readiness, and regulatory needs.",
    stage: "Assess",
  },
  {
    key: "implementation",
    title: "Starter Implementation",
    price: "$9,999",
    subtitle: "3 Workflow Lite automations with measurable outcomes",
    output:
      "3 workflow automations + Milestones + Timeline + KPI dashboard + Value tracking",
    stage: "Build",
  },
  {
    key: "retainer",
    title: "CAIO Retainer",
    price: "$5,995/mo",
    subtitle: "Ongoing optimization, monitoring, and expansion",
    output:
      "Monitoring + Optimization + Quarterly roadmap refresh + 1 micro-enhancement/month",
    stage: "Scale",
  },
];

export const METHODOLOGY = [
  {
    step: "Discover",
    detail:
      "Surface high-ROI workflow opportunities across your existing business systems.",
  },
  {
    step: "Assess",
    detail:
      "Quantify impact, effort, and risk with a structured evaluation framework.",
  },
  {
    step: "Implement",
    detail:
      "Deliver secure workflow automations with clear milestones and approvals.",
  },
  {
    step: "Expand",
    detail:
      "Validate value and scale through ongoing retainer support.",
  },
];

export const SECURITY_CHECKLIST = [
  "Staff can only access data you want them to see - Using Role-based access and tenant isolation",
  "See who has changed what - Full Audit trail for changes and AI outputs",
  "You control what is automated - No automated production changes without written approval",
  "A Mission Control Dashboard - Critical information delivered in your portal + PDF reoprts",
  "AI Enabled apps integrated into your business - Work within approved tools; custom agent coding as required",
];

export const ANALYTICS_EVENTS = [
  "form_submit",
  "booked_call",
  "attended_call",
  "paid_assessment",
  "assessment_complete",
  "action_plan_purchased",
  "action_plan_delivered",
  "sow_signed",
  "deposit_paid",
  "implementation_started",
];

export const trackEvent = (name, payload = {}) => {
  const entry = { id: uid("evt"), name, payload, ts: nowIso() };
  const raw = localStorage.getItem(LS_ANALYTICS_KEY);
  const arr = raw ? JSON.parse(raw) : [];
  arr.unshift(entry);
  localStorage.setItem(LS_ANALYTICS_KEY, JSON.stringify(arr.slice(0, 200)));
  // eslint-disable-next-line no-console
  console.log("[analytics]", name, payload);
  return entry;
};

export const analytics = {
  list: () => {
    const raw = localStorage.getItem(LS_ANALYTICS_KEY);
    return raw ? JSON.parse(raw) : [];
  },
  clear: () => localStorage.removeItem(LS_ANALYTICS_KEY),
};

export const mockAuth = {
  getSession: () => {
    const raw = localStorage.getItem(LS_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  signIn: async ({ role = "consultant", full_name = "Consultant" } = {}) => {
    const session = {
      user: {
        id: role === "admin" ? "user_admin" : role === "client" ? "user_client" : "user_consultant",
        full_name,
        role, // admin | consultant | client
      },
      tenant: { id: "tenant_1", name: "589 Labs.ai" },
      created_at: nowIso(),
    };
    localStorage.setItem(LS_SESSION_KEY, JSON.stringify(session));
    return session;
  },
  signOut: async () => {
    localStorage.removeItem(LS_SESSION_KEY);
  },
};

// --- CRM-lite MOCK service
const DEMO_SLA_MS = 30_000; // demo escalation window (30 seconds) for Phase 1 MOCK

const notify = (db, notif) => {
  const n = {
    id: uid("ntf"),
    tenant_id: db.tenant.id,
    created_at: nowIso(),
    read_at: null,
    ...notif,
  };
  db.notifications.unshift(n);
  return n;
};

const escalateLeadIfNeeded = (leadId) => {
  // client-side “scheduler”: checks lead response and escalates.
  const db = loadDb();
  const lead = db.leads.find((l) => l.id === leadId);
  if (!lead) return;
  if (lead.first_response_at) return;

  const created = new Date(lead.created_at).getTime();
  if (Date.now() - created < DEMO_SLA_MS) return;

  const existing = db.notifications.find(
    (n) => n.type === "sla_escalation" && n.meta?.lead_id === leadId,
  );
  if (existing) return;

  notify(db, {
    type: "sla_escalation",
    title: "SLA escalation: lead not contacted",
    body: `No first response logged for ${lead.full_name} (${lead.company}).`,
    meta: { lead_id: leadId },
  });
  saveDb(db);
};

export const crm = {
  listLeads: ({ tenant_id } = {}) => {
    const db = loadDb();
    return db.leads.filter((l) => !tenant_id || l.tenant_id === tenant_id);
  },

  createLead: async ({
    tenant_id = "tenant_1",
    source = "website",
    utm = { source: "direct", medium: "none", campaign: "" },
    vertical,
    company,
    full_name,
    email,
    phone,
    notes,
  }) => {
    const db = loadDb();
    const lead = {
      id: uid("lead"),
      tenant_id,
      created_at: nowIso(),
      updated_at: nowIso(),
      source,
      utm,
      vertical,
      company,
      full_name,
      email,
      phone,
      notes,
      status: "new",
      first_response_at: null,
    };
    db.leads.unshift(lead);

    notify(db, {
      type: "new_lead",
      title: "New lead submitted",
      body: `${full_name} (${company}) requested an assessment.`,
      meta: { lead_id: lead.id },
    });

    saveDb(db);

    // Demo escalation timer
    setTimeout(() => escalateLeadIfNeeded(lead.id), DEMO_SLA_MS + 500);

    return lead;
  },

  markFirstResponse: async ({ lead_id, user_id }) => {
    const db = loadDb();
    const lead = db.leads.find((l) => l.id === lead_id);
    if (!lead) throw new Error("Lead not found");
    lead.first_response_at = nowIso();
    lead.updated_at = nowIso();

    notify(db, {
      type: "first_response",
      title: "First response logged",
      body: `First response logged by ${user_id}.`,
      meta: { lead_id },
    });

    saveDb(db);
    return lead;
  },

  convertLeadToProspect: async ({ lead_id, assigned_to_user_id = "user_consultant" }) => {
    const db = loadDb();
    const lead = db.leads.find((l) => l.id === lead_id);
    if (!lead) throw new Error("Lead not found");

    const accountId = uid("acct");
    const contactId = uid("ct");
    db.accounts.unshift({
      id: accountId,
      tenant_id: lead.tenant_id,
      name: lead.company,
      industry: lead.vertical,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    db.contacts.unshift({
      id: contactId,
      tenant_id: lead.tenant_id,
      account_id: accountId,
      full_name: lead.full_name,
      title: "",
      email: lead.email,
      phone: lead.phone,
      created_at: nowIso(),
      updated_at: nowIso(),
    });

    const prospect = {
      id: uid("pros"),
      tenant_id: lead.tenant_id,
      account_id: accountId,
      primary_contact_id: contactId,
      assigned_to_user_id,
      stage: "new",
      created_at: nowIso(),
      updated_at: nowIso(),
      flags: { security_sensitive: true, regulated: false },
      payment: {
        detailed_assessment_paid: false,
        roadmap_paid: false,
        deposit_paid: false,
      },
    };

    db.prospects.unshift(prospect);
    lead.status = "converted";
    lead.updated_at = nowIso();

    notify(db, {
      type: "prospect_created",
      title: "Lead qualified → Prospect created",
      body: `${lead.company} is now a prospect in stage “new”.`,
      meta: { prospect_id: prospect.id },
    });

    saveDb(db);
    return prospect;
  },

  listProspects: ({ tenant_id, assigned_to_user_id } = {}) => {
    const db = loadDb();
    return db.prospects
      .filter((p) => !tenant_id || p.tenant_id === tenant_id)
      .filter((p) => !assigned_to_user_id || p.assigned_to_user_id === assigned_to_user_id);
  },

  getProspect: ({ prospect_id }) => {
    const db = loadDb();
    const p = db.prospects.find((x) => x.id === prospect_id);
    if (!p) return null;
    const account = db.accounts.find((a) => a.id === p.account_id);
    const contact = db.contacts.find((c) => c.id === p.primary_contact_id);
    const interactions = db.interactions.filter((i) => i.prospect_id === p.id);
    const meetings = db.meetings.filter((m) => m.prospect_id === p.id);
    return { ...p, account, contact, interactions, meetings };
  },

  updateProspectStage: async ({ prospect_id, stage }) => {
    const db = loadDb();
    const p = db.prospects.find((x) => x.id === prospect_id);
    if (!p) throw new Error("Prospect not found");
    p.stage = stage;
    p.updated_at = nowIso();
    saveDb(db);
    return p;
  },

  logInteraction: async ({ prospect_id, type = "note", summary, created_by }) => {
    const db = loadDb();
    db.interactions.unshift({
      id: uid("ix"),
      tenant_id: db.tenant.id,
      prospect_id,
      type,
      created_at: nowIso(),
      created_by,
      summary,
    });
    saveDb(db);
  },

  simulateBookInitialCall: async ({ prospect_id }) => {
    const db = loadDb();
    db.meetings.unshift({
      id: uid("mtg"),
      tenant_id: db.tenant.id,
      prospect_id,
      type: "initial_free_call",
      scheduled_at: new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString(),
      status: "scheduled",
      calendly_event_id: uid("mock_evt"),
      zoom_link: "",
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    const p = db.prospects.find((x) => x.id === prospect_id);
    if (p) p.stage = "scheduled";
    saveDb(db);
  },

  simulatePayment: async ({ prospect_id, product_key }) => {
    const db = loadDb();
    const p = db.prospects.find((x) => x.id === prospect_id);
    if (!p) throw new Error("Prospect not found");
    if (product_key === "detailed") {
      p.payment.detailed_assessment_paid = true;
      p.stage = "paid_assessment";
      trackEvent("paid_assessment", { prospect_id });
    }
    if (product_key === "roadmap") p.payment.roadmap_paid = true;
    if (product_key === "deposit") p.payment.deposit_paid = true;
    p.updated_at = nowIso();
    saveDb(db);
    return p;
  },

  listNotifications: ({ tenant_id } = {}) => {
    const db = loadDb();
    return db.notifications.filter((n) => !tenant_id || n.tenant_id === tenant_id);
  },

  markNotificationRead: async ({ notification_id }) => {
    const db = loadDb();
    const n = db.notifications.find((x) => x.id === notification_id);
    if (!n) return;
    n.read_at = nowIso();
    saveDb(db);
  },
};

// --- Assessments (MOCK)
const Q = (id, text, options, category = "general") => ({
  id,
  text,
  options,
  category,
});

export const QUESTIONNAIRES = {
  initial_15: {
    id: "initial_15",
    title: "Preliminary Assessment (15 Questions)",
    categories: ["systems", "process", "data", "risk", "value"],
    questions: [
      Q("q1", "Which core systems run your workflows?", ["ERP", "CRM", "HCM", "All three", "Other"], "systems"),
      Q("q2", "How often do exceptions require manual intervention?", ["Rare", "Weekly", "Daily", "Hourly"], "process"),
      Q("q3", "Are SOPs documented for the target workflows?", ["No", "Partial", "Yes"], "process"),
      Q("q4", "Where is workflow data stored?", ["Single system", "Multiple systems", "Spreadsheets/Email"], "data"),
      Q("q5", "Data quality confidence?", ["Low", "Medium", "High"], "data"),
      Q("q6", "Any regulated data (PHI/PCI/etc.)?", ["No", "Yes"], "risk"),
      Q("q7", "Access controls maturity?", ["Basic", "Role-based", "Fine-grained + auditing"], "risk"),
      Q("q8", "Is there an approval process for production changes?", ["No", "Sometimes", "Yes"], "risk"),
      Q("q9", "Time spent weekly on repetitive workflow work?", ["<5h", "5–20h", "20–50h", ">50h"], "value"),
      Q("q10", "Biggest friction point today?", ["Intake", "Routing", "Approvals", "Reporting", "Other"], "value"),
      Q("q11", "Do teams use a knowledge base?", ["No", "Some docs", "Yes"], "systems"),
      Q("q12", "How many departments touch the workflow?", ["1", "2–3", "4+"], "process"),
      Q("q13", "Do you have internal technical owners?", ["No", "Some", "Yes"], "systems"),
      Q("q14", "How important is measurable ROI tracking?", ["Nice to have", "Important", "Critical"], "value"),
      Q("q15", "Timeline to see results?", ["30 days", "90 days", "6 months"], "value"),
    ],
  },
  detailed_50: {
    id: "detailed_50",
    title: "In-Depth Assessment (50 Questions)",
    categories: ["systems", "process", "data", "risk", "value"],
    // Keep this compact for Phase 1; Phase 2 will expand templates + weights.
    questions: Array.from({ length: 50 }).map((_, idx) => {
      const i = idx + 1;
      const category = ["systems", "process", "data", "risk", "value"][idx % 5];
      return Q(
        `d${i}`,
        `Detailed Q${i}: ${category} readiness checkpoint`,
        ["Low", "Medium", "High"],
        category,
      );
    }),
  },
};

export const assessment = {
  getResponseSet: ({ prospect_id, template_id }) => {
    const db = loadDb();
    const rs = db.response_sets.find(
      (x) => x.prospect_id === prospect_id && x.template_id === template_id,
    );
    if (!rs) return null;

    const responses = db.responses
      .filter((r) => r.response_set_id === rs.id)
      .reduce((acc, r) => {
        acc[r.question_id] = r;
        return acc;
      }, {});

    const score = db.score_summaries.find((s) => s.response_set_id === rs.id) || null;
    return { ...rs, responses, score };
  },

  upsertResponse: async ({
    prospect_id,
    template_id,
    question_id,
    value,
    notes = "",
    updated_by,
  }) => {
    const db = loadDb();

    let rs = db.response_sets.find(
      (x) => x.prospect_id === prospect_id && x.template_id === template_id,
    );
    if (!rs) {
      rs = {
        id: uid("rs"),
        tenant_id: db.tenant.id,
        prospect_id,
        template_id,
        status: "in_progress", // in_progress | locked
        created_at: nowIso(),
        updated_at: nowIso(),
        locked_at: null,
      };
      db.response_sets.unshift(rs);
    }

    if (rs.status === "locked") throw new Error("This assessment is locked.");

    let r = db.responses.find(
      (x) => x.response_set_id === rs.id && x.question_id === question_id,
    );
    if (!r) {
      r = {
        id: uid("resp"),
        tenant_id: db.tenant.id,
        response_set_id: rs.id,
        question_id,
        value,
        notes,
        created_at: nowIso(),
        updated_at: nowIso(),
        updated_by,
      };
      db.responses.unshift(r);
    } else {
      r.value = value;
      r.notes = notes;
      r.updated_by = updated_by;
      r.updated_at = nowIso();
    }

    rs.updated_at = nowIso();

    // Simple scoring for Phase 1 demo
    const template = QUESTIONNAIRES[template_id];
    const all = db.responses.filter((x) => x.response_set_id === rs.id);
    const answered = all.length;
    const total = template.questions.length;
    const completion = Math.round((answered / total) * 100);

    const score = {
      id: uid("score"),
      tenant_id: db.tenant.id,
      response_set_id: rs.id,
      completion,
      flags: {
        regulated: template_id === "initial_15" ? all.some((x) => x.value === "Yes") : false,
      },
      updated_at: nowIso(),
    };
    db.score_summaries = db.score_summaries.filter((s) => s.response_set_id !== rs.id);
    db.score_summaries.unshift(score);

    saveDb(db);

    return { response_set: rs, response: r, score };
  },

  lockResponseSet: async ({ response_set_id, locked_by }) => {
    const db = loadDb();
    const rs = db.response_sets.find((x) => x.id === response_set_id);
    if (!rs) throw new Error("Response set not found");
    rs.status = "locked";
    rs.locked_at = nowIso();
    rs.updated_at = nowIso();

    notify(db, {
      type: "assessment_locked",
      title: "Assessment locked",
      body: `Assessment locked by ${locked_by}.`,
      meta: { response_set_id },
    });

    trackEvent("assessment_complete", { response_set_id });

    saveDb(db);
    return rs;
  },
};

export const resetMockData = () => {
  localStorage.removeItem(LS_KEY);
  localStorage.removeItem(LS_SESSION_KEY);
  localStorage.removeItem(LS_ANALYTICS_KEY);
};
